<template>
  <v-container class="management-container">

    <!-- Header mit Titel und Tabs -->
    <v-card class="main-card mb-4">

      <v-tabs
        v-model="activeTab"
        color="success"
        align-tabs="center"
        class="management-tabs"
      >
        <v-tab value="summary" class="tab-item">
          <v-icon size="small" start>mdi-account-details</v-icon>
          Mitarbeiter-Übersicht
        </v-tab>
        <v-tab value="ranking" class="tab-item">
          <v-icon>mdi-poll</v-icon>
          Leistungsübersichten
        </v-tab>
        <v-tab value="comparison" class="tab-item">
          <v-icon>mdi-compare</v-icon>
          Direktvergleich
        </v-tab>
      </v-tabs>

      <v-card-text>
        <!-- Filter-Bereich -->
        <v-card class="filter-card mb-4">
          <v-card-title class="filter-title">
            <v-icon color="success" class="mr-2">mdi-filter</v-icon>
            Filter und Zeitraum
          </v-card-title>
          <v-card-text>
            <div class="filter-controls">
              <!-- Zeitraum-Auswahl -->
              <div class="timeframe-selector">
                <v-select
                  v-model="timeframe"
                  :items="timeframeOptions"
                  label="Zeitraum"
                  density="comfortable"
                  variant="outlined"
                  class="timeframe-select form-field"
                  color="success"
                ></v-select>

                <template v-if="timeframe === 'month'">
                  <v-select
                    v-model="selectedMonth"
                    :items="monthOptions"
                    label="Monat"
                    density="comfortable"
                    variant="outlined"
                    class="month-select mr-2 form-field"
                    color="success"
                  ></v-select>
                  <v-select
                    v-model="selectedYear"
                    :items="yearOptions"
                    label="Jahr"
                    density="comfortable"
                    variant="outlined"
                    class="year-select form-field"
                    color="success"
                  ></v-select>
                </template>

                <template v-else-if="timeframe === 'quarter'">
                  <v-select
                    v-model="selectedQuarter"
                    :items="quarterOptions"
                    label="Quartal"
                    density="comfortable"
                    variant="outlined"
                    class="quarter-select mr-2 form-field"
                    color="success"
                  ></v-select>
                  <v-select
                    v-model="selectedYear"
                    :items="yearOptions"
                    label="Jahr"
                    density="comfortable"
                    variant="outlined"
                    class="year-select form-field"
                    color="success"
                  ></v-select>
                </template>

                <template v-else-if="timeframe === 'year'">
                  <v-select
                    v-model="selectedYear"
                    :items="yearOptions"
                    label="Jahr"
                    density="comfortable"
                    variant="outlined"
                    class="year-select-wide form-field"
                    color="success"
                  ></v-select>
                </template>

                <template v-else-if="timeframe === 'custom'">
                  <v-text-field
                    v-model="customStartDate"
                    label="Von"
                    type="date"
                    density="comfortable"
                    variant="outlined"
                    class="date-select mr-2 form-field"
                    color="success"
                  ></v-text-field>
                  <v-text-field
                    v-model="customEndDate"
                    label="Bis"
                    type="date"
                    density="comfortable"
                    variant="outlined"
                    class="date-select form-field"
                    color="success"
                  ></v-text-field>
                </template>
              </div>

              <!-- Benutzer-Auswahl (in Direktvergleich & Benutzerauswertungen) -->
              <template v-if="activeTab !== 'ranking'">
                <div class="user-selector">
                  <div class="selector-header">
                    <span class="selector-title">Benutzerauswahl</span>
                    <v-btn
                      v-if="activeTab === 'summary'"
                      size="small"
                      variant="text"
                      color="success"
                      class="show-more-button"
                      @click="selectAllUsers"
                    >
                      Alle auswählen
                    </v-btn>
                  </div>
                  <v-autocomplete
                    v-if="activeTab === 'summary'"
                    v-model="selectedUser"
                    :items="users"
                    item-title="name"
                    item-value="id"
                    label="Benutzer auswählen"
                    density="comfortable"
                    variant="outlined"
                    clearable
                    placeholder="Alle Benutzer anzeigen..."
                    class="form-field"
                    color="success"
                  ></v-autocomplete>
                  <v-select
                    v-if="activeTab === 'comparison'"
                    v-model="selectedUsers"
                    :items="users"
                    item-title="name"
                    item-value="id"
                    label="Benutzer zum Vergleich"
                    density="comfortable"
                    variant="outlined"
                    multiple
                    chips
                    closable-chips
                    class="form-field"
                    color="success"
                  ></v-select>
                </div>
              </template>

              <!-- Aktion-Buttons -->
              <div class="action-buttons">
                <v-btn
                  color="success"
                  :loading="loading"
                  @click="fetchData"
                  class="mr-2 refresh-btn"
                >
                  <v-icon left>mdi-reload</v-icon>
                  Aktualisieren
                </v-btn>
                <v-btn
                  v-if="hasData"
                  color="info"
                  @click="exportPdf"
                  class="export-btn mr-2"
                >
                  <v-icon left>mdi-file-export</v-icon>
                  Als PDF exportieren
                </v-btn>
                <v-btn
                  v-if="activeTab === 'summary' && hasData"
                  color="primary"
                  :loading="csvLoading"
                  @click="exportCsv"
                  class="export-btn"
                >
                  <v-icon left>mdi-download</v-icon>
                  CSV Download
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Loading-Overlay -->
        <div v-if="loading" class="loading-overlay">
          <v-progress-circular
            indeterminate
            color="success"
            size="64"
          ></v-progress-circular>
          <span class="loading-text">Daten werden geladen...</span>
        </div>

        <!-- Platzhalter für den Content-Bereich -->
        <div v-else-if="!hasData" class="no-data-card">
          <v-icon size="64" color="grey">mdi-chart-box-outline</v-icon>
          <h3>Keine Daten verfügbar</h3>
          <p>
            Bitte wählen Sie einen Zeitraum und klicken Sie auf "Aktualisieren",
            um Daten anzuzeigen.
          </p>
        </div>

        <!-- Tab-Inhalte basierend auf activeTab -->
        <div v-if="hasData && !loading" class="tab-content-area">
          <!-- Tab: Benutzerauswertungen -->
          <div v-if="activeTab === 'summary'" class="summary-tab">
            <v-card class="mb-4 summary-header-card">
              <v-card-title>
                <v-icon color="success" class="mr-2"
                  >mdi-account-details</v-icon
                >
                {{
                  selectedUser
                    ? `Auswertung für ${getUserName(selectedUser)}`
                    : "Auswertung für alle Benutzer"
                }}
              </v-card-title>
              <v-card-subtitle>
                {{ getTimeframeText() }}
              </v-card-subtitle>
            </v-card>

            <div class="user-metrics-grid">
              <template v-if="summaryData">
                <!-- Pro Benutzer eine Karte mit Metriken -->
                <v-card
                  v-for="userData in summaryData.results"
                  :key="userData.id"
                  class="user-metrics-card"
                >
                  <v-card-title class="user-card-header">
                    <v-avatar color="success" size="40" class="mr-3">
                      {{
                        userData.name
                          ? userData.name.charAt(0).toUpperCase()
                          : "U"
                      }}
                    </v-avatar>
                    <span
                      >{{ userData.name }}
                      <span
                        v-if="userData.mitarbeiternummer"
                        class="user-id-inline"
                        >({{ userData.mitarbeiternummer }})</span
                      ></span
                    >
                  </v-card-title>

                  <v-card-text>
                    <!-- Instandsetzungen -->
                    <div
                      class="metrics-section"
                      v-if="userData.instandsetzungen.length > 0"
                    >
                      <h3 class="metrics-title">
                        <v-icon color="blue" size="small" class="mr-1"
                          >mdi-wrench</v-icon
                        >
                        Instandsetzungen
                      </h3>
                      <div class="metrics-list">
                        <div
                          v-for="(item, i) in userData.instandsetzungen"
                          :key="i"
                          class="metric-item"
                        >
                          <v-tooltip location="top">
                            <template v-slot:activator="{ props }">
                              <span v-bind="props" class="metric-name hoverable">
                                {{ item.geraetetyp }}
                                <v-icon size="small" class="ml-1 info-icon">mdi-information-outline</v-icon>
                              </span>
                            </template>
                            <div class="tooltip-content">
                              <div class="tooltip-header">
                                <strong>{{ item.geraetetyp }} - Instandsetzungen</strong>
                              </div>
                              <div class="tooltip-body">
                                <div
                                  v-for="reparaturtyp in item.reparaturtypen"
                                  :key="reparaturtyp.typ"
                                  class="tooltip-item"
                                >
                                  <span class="tooltip-label">{{ reparaturtyp.typ }}:</span>
                                  <span class="tooltip-value">{{ reparaturtyp.menge }} Stk.</span>
                                </div>
                              </div>
                            </div>
                          </v-tooltip>
                          <span class="metric-value"
                            >{{ item.menge }} Stk.</span
                          >
                        </div>
                        <div class="metric-total">
                          <span class="total-label">Gesamt</span>
                          <span class="total-value"
                            >{{ getTotalInstandsetzungen(userData) }} Stk.</span
                          >
                        </div>
                      </div>
                    </div>

                    <!-- Retouren -->
                    <div
                      class="metrics-section"
                      v-if="userData.retouren.length > 0"
                    >
                      <h3 class="metrics-title">
                        <v-icon color="red" size="small" class="mr-1"
                          >mdi-package-variant</v-icon
                        >
                        Retouren
                      </h3>
                      <div class="metrics-list">
                        <div
                          v-for="(item, i) in userData.retouren"
                          :key="i"
                          class="metric-item"
                        >
                          <v-tooltip location="top">
                            <template v-slot:activator="{ props }">
                              <span v-bind="props" class="metric-name hoverable">
                                {{ item.geraetetyp }}
                                <v-icon size="small" class="ml-1 info-icon">mdi-information-outline</v-icon>
                              </span>
                            </template>
                            <div class="tooltip-content">
                              <div class="tooltip-header">
                                <strong>{{ item.geraetetyp }} - Retouren</strong>
                              </div>
                              <div class="tooltip-body">
                                <div
                                  v-for="retourentyp in item.retourentypen"
                                  :key="retourentyp.typ"
                                  class="tooltip-item"
                                >
                                  <span class="tooltip-label">{{ retourentyp.typ }}:</span>
                                  <span class="tooltip-value">{{ retourentyp.menge }} Stk.</span>
                                </div>
                              </div>
                            </div>
                          </v-tooltip>
                          <span class="metric-value"
                            >{{ item.menge }} Stk.</span
                          >
                        </div>
                        <div class="metric-total">
                          <span class="total-label">Gesamt</span>
                          <span class="total-value"
                            >{{ getTotalRetouren(userData) }} Stk.</span
                          >
                        </div>
                      </div>
                    </div>

                    <!-- Nebentätigkeiten -->
                    <div
                      class="metrics-section"
                      v-if="userData.nebentaetigkeiten.length > 0"
                    >
                      <h3 class="metrics-title">
                        <v-icon color="amber" size="small" class="mr-1"
                          >mdi-clock-outline</v-icon
                        >
                        Nebentätigkeiten
                      </h3>
                      <div class="metrics-list">
                        <div
                          v-for="(item, i) in userData.nebentaetigkeiten"
                          :key="i"
                          class="metric-item"
                        >
                          <span class="metric-name">{{
                            item.kommentar || "Ohne Bezeichnung"
                          }}</span>
                          <span class="metric-value">{{
                            formatDuration(item.dauer)
                          }}</span>
                        </div>
                        <div class="metric-total">
                          <span class="total-label">Gesamt</span>
                          <span class="total-value">{{
                            formatDuration(getTotalNebentaetigkeiten(userData))
                          }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Details anzeigen Button -->
                    <v-btn
                      @click="showUserDetails(userData)"
                      color="success"
                      variant="tonal"
                      block
                      class="mt-4 mb-2"
                      prepend-icon="mdi-clipboard-list-outline">
                      Auftragsdetails anzeigen
                    </v-btn>

                    <!-- Trend-Analyse Widget für einzelne Mitarbeiter -->
                    <div class="trend-section mt-4">
                      <ManagementTrendWidget
                        :employee="{
                          id: userData.id,
                          name: userData.name,
                          loginname: userData.loginname
                        }"
                        :timeframe="timeframe"
                        :selected-month="selectedMonth"
                        :selected-year="selectedYear"
                      />
                    </div>

                    <!-- Keine Daten Hinweis -->
                    <div
                      v-if="
                        !userData.instandsetzungen.length &&
                        !userData.retouren.length &&
                        !userData.nebentaetigkeiten.length
                      "
                      class="no-metrics"
                    >
                      <v-icon size="40" color="grey"
                        >mdi-alert-circle-outline</v-icon
                      >
                      <p>Keine Daten für diesen Zeitraum verfügbar.</p>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Gesamtzusammenfassung für alle Benutzer -->
                <v-card
                  v-if="!selectedUser && summaryData.totals"
                  class="user-metrics-card summary-totals"
                >
                  <v-card-title class="totals-header">
                    <v-icon color="success" size="28" class="mr-2"
                      >mdi-chart-line</v-icon
                    >
                    Gesamtzusammenfassung
                  </v-card-title>

                  <v-card-text>
                    <div class="totals-grid">
                      <div class="total-box">
                        <div class="total-icon">
                          <v-icon color="blue">mdi-wrench</v-icon>
                        </div>
                        <div class="total-data">
                          <span class="total-value">{{
                            summaryData.totals.instandsetzungen_total
                          }}</span>
                          <span class="total-label">Instandsetzungen</span>
                        </div>
                      </div>

                      <div class="total-box">
                        <div class="total-icon">
                          <v-icon color="red">mdi-package-variant</v-icon>
                        </div>
                        <div class="total-data">
                          <span class="total-value">{{
                            summaryData.totals.retouren_total
                          }}</span>
                          <span class="total-label">Retouren</span>
                        </div>
                      </div>

                      <div class="total-box">
                        <div class="total-icon">
                          <v-icon color="amber">mdi-clock-outline</v-icon>
                        </div>
                        <div class="total-data">
                          <span class="total-value">{{
                            formatDuration(
                              summaryData.totals.nebentaetigkeiten_minuten_total
                            )
                          }}</span>
                          <span class="total-label">Nebentätigkeiten</span>
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </template>
            </div>
          </div>

          <!-- Tab: Leistungsübersichten -->
          <div v-if="activeTab === 'ranking'" class="ranking-tab">
            <v-card class="mb-4 ranking-header-card">
              <v-card-title class="ranking-title">
                <v-icon color="success" class="mr-2">mdi-poll</v-icon>
                Leistungsübersichten für {{ getTimeframeText() }}
              </v-card-title>

              <v-card-text>
                <v-tabs
                  v-model="activeRankingTab"
                  grow
                  color="success"
                  class="ranking-tab-nav"
                >
                  <v-tab value="instandsetzungen" class="tab-item"
                    >Instandsetzungen</v-tab
                  >
                  <v-tab value="retouren" class="tab-item">Retouren</v-tab>
                  <v-tab value="nebentaetigkeiten" class="tab-item"
                    >Nebentätigkeiten</v-tab
                  >
                  <v-tab value="gesamt" class="tab-item">Gesamtranking</v-tab>
                </v-tabs>
              </v-card-text>
            </v-card>

            <v-window v-model="activeRankingTab">
              <!-- Instandsetzungen Ranking -->
              <v-window-item value="instandsetzungen">
                <v-card class="ranking-table-card">
                  <v-card-title class="ranking-table-title">
                    <v-icon color="blue" class="mr-2">mdi-wrench</v-icon>
                    Leistungsübersicht: Instandsetzungen
                  </v-card-title>

                  <div class="table-container">
                    <v-data-table
                      :headers="instandsetzungenHeaders"
                      :items="rankingData?.rankings?.instandsetzungen || []"
                      :items-per-page="10"
                      class="ranking-table"
                    >
                      <template #[`item.position`]="{ index }">
                        <div class="rank-cell" :class="getRankClass(index)">
                          {{ index + 1 }}
                        </div>
                      </template>

                      <template #[`item.name`]="{ item }">
                        {{ item.vorname }} {{ item.nachname }}
                      </template>
                    </v-data-table>
                  </div>
                </v-card>
              </v-window-item>

              <!-- Retouren Ranking -->
              <v-window-item value="retouren">
                <v-card class="ranking-table-card">
                  <v-card-title class="ranking-table-title">
                    <v-icon color="red" class="mr-2"
                      >mdi-package-variant</v-icon
                    >
                    Leistungsübersicht: Retouren
                  </v-card-title>

                  <div class="table-container">
                    <v-data-table
                      :headers="retourenHeaders"
                      :items="rankingData?.rankings?.retouren || []"
                      :items-per-page="10"
                      class="ranking-table"
                    >
                      <template #[`item.position`]="{ index }">
                        <div class="rank-cell" :class="getRankClass(index)">
                          {{ index + 1 }}
                        </div>
                      </template>

                      <template #[`item.name`]="{ item }">
                        {{ item.vorname }} {{ item.nachname }}
                      </template>
                    </v-data-table>
                  </div>
                </v-card>
              </v-window-item>

              <!-- Nebentätigkeiten Ranking -->
              <v-window-item value="nebentaetigkeiten">
                <v-card class="ranking-table-card">
                  <v-card-title class="ranking-table-title">
                    <v-icon color="amber" class="mr-2"
                      >mdi-clock-outline</v-icon
                    >
                    Leistungsübersicht: Nebentätigkeiten
                  </v-card-title>

                  <div class="table-container">
                    <v-data-table
                      :headers="nebentaetigkeitenHeaders"
                      :items="rankingData?.rankings?.nebentaetigkeiten || []"
                      :items-per-page="10"
                      class="ranking-table"
                    >
                      <template #[`item.position`]="{ index }">
                        <div class="rank-cell" :class="getRankClass(index)">
                          {{ index + 1 }}
                        </div>
                      </template>

                      <template #[`item.name`]="{ item }">
                        {{ item.vorname }} {{ item.nachname }}
                      </template>

                      <template #[`item.gesamt_minuten`]="{ item }">
                        {{ formatDuration(item.gesamt_minuten) }}
                      </template>
                    </v-data-table>
                  </div>
                </v-card>
              </v-window-item>

              <!-- Gesamtranking -->
              <v-window-item value="gesamt">
                <v-card class="ranking-table-card">
                  <v-card-title class="ranking-table-title">
                    <v-icon color="success" class="mr-2">mdi-trophy</v-icon>
                    Gesamtleistungsübersicht
                  </v-card-title>

                  <div class="table-container">
                    <v-data-table
                      :headers="gesamtHeaders"
                      :items="rankingData?.rankings?.gesamt || []"
                      :items-per-page="10"
                      class="ranking-table"
                    >
                      <template #[`item.position`]="{ index }">
                        <div class="rank-cell" :class="getRankClass(index)">
                          {{ index + 1 }}
                        </div>
                      </template>

                      <template #[`item.name`]="{ item }">
                        {{ item.vorname }} {{ item.nachname }}
                      </template>

                      <template #[`item.nebentaetigkeiten_minuten`]="{ item }">
                        {{ formatDuration(item.nebentaetigkeiten_minuten) }}
                      </template>
                    </v-data-table>
                  </div>
                </v-card>
              </v-window-item>
            </v-window>

            <!-- Gerätetyp-spezifische Rankings -->
            <v-card
              v-if="rankingData?.geraetetyp_statistik"
              class="mt-4 device-type-card"
            >
              <v-card-title>
                <v-icon color="success" class="mr-2">mdi-shape-outline</v-icon>
                Leistungsübersichten nach Gerätetyp
              </v-card-title>

              <v-card-text>
                <v-expansion-panels>
                  <v-expansion-panel
                    v-for="(
                      rankings, geraetetyp
                    ) in rankingData.geraetetyp_statistik"
                    :key="geraetetyp"
                    class="device-expansion-panel"
                  >
                    <v-expansion-panel-title>
                      <strong>{{ geraetetyp }}</strong> ({{ rankings.length }}
                      Benutzer)
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <v-data-table
                        :headers="geraetetypHeaders"
                        :items="rankings"
                        :items-per-page="5"
                        class="ranking-table"
                      >
                        <template #[`item.position`]="{ index }">
                          <div class="rank-cell" :class="getRankClass(index)">
                            {{ index + 1 }}
                          </div>
                        </template>
                      </v-data-table>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>
          </div>

          <!-- Tab: Direktvergleich -->
          <div v-if="activeTab === 'comparison'" class="comparison-tab">
            <!-- Hinweis wenn weniger als 2 Benutzer ausgewählt -->
            <v-alert
              v-if="selectedUsers.length < 2"
              type="info"
              icon="mdi-account-question"
              variant="tonal"
              class="mb-4"
            >
              Bitte wählen Sie mindestens zwei Benutzer aus, um einen Vergleich
              durchzuführen.
            </v-alert>

            <template v-else>
              <!-- Vergleichkarten -->
              <v-card class="comparison-card mb-4">
                <v-card-title>
                  <v-icon color="success" class="mr-2">mdi-compare</v-icon>
                  Direktvergleich für {{ getTimeframeText() }}
                </v-card-title>

                <!-- Tabs für verschiedene Vergleichskategorien -->
                <v-card-text>
                  <v-tabs
                    v-model="activeComparisonTab"
                    color="success"
                    grow
                    class="comparison-tab-nav"
                  >
                    <v-tab value="overview" class="tab-item">Übersicht</v-tab>
                    <v-tab value="instandsetzungen" class="tab-item"
                      >Instandsetzungen</v-tab
                    >
                    <v-tab value="retouren" class="tab-item">Retouren</v-tab>
                    <v-tab value="nebentaetigkeiten" class="tab-item"
                      >Nebentätigkeiten</v-tab
                    >
                  </v-tabs>

                  <v-window v-model="activeComparisonTab">
                    <!-- Übersichts-Tab -->
                    <v-window-item value="overview">
                      <div
                        v-if="comparisonData && comparisonData.users"
                        class="comparison-overview"
                      >
                        <v-card
                          v-for="user in comparisonData.users"
                          :key="user.id"
                          class="user-comparison-card"
                        >
                          <v-card-title class="user-card-header">
                            <v-avatar color="success" size="40" class="mr-3">
                              {{
                                user.name
                                  ? user.name.charAt(0).toUpperCase()
                                  : "U"
                              }}
                            </v-avatar>
                            <span>{{ user.name }}</span>
                          </v-card-title>

                          <v-card-text class="user-comparison-summary">
                            <div class="comparison-metric">
                              <span class="metric-label">
                                <v-icon color="blue" size="small"
                                  >mdi-wrench</v-icon
                                >
                                Instandsetzungen
                              </span>
                              <span class="metric-value">{{
                                user.totals.instandsetzungen || 0
                              }}</span>
                            </div>

                            <div class="comparison-metric">
                              <span class="metric-label">
                                <v-icon color="red" size="small"
                                  >mdi-package-variant</v-icon
                                >
                                Retouren
                              </span>
                              <span class="metric-value">{{
                                user.totals.retouren || 0
                              }}</span>
                            </div>

                            <div class="comparison-metric">
                              <span class="metric-label">
                                <v-icon color="amber" size="small"
                                  >mdi-clock-outline</v-icon
                                >
                                Nebentätigkeiten
                              </span>
                              <span class="metric-value">{{
                                formatDuration(
                                  user.totals.nebentaetigkeiten_minuten
                                )
                              }}</span>
                            </div>

                            <div class="comparison-metric total">
                              <span class="metric-label">
                                <v-icon color="success" size="small"
                                  >mdi-calculator</v-icon
                                >
                                Gesamt
                              </span>
                              <span class="metric-value"
                                >{{
                                  (user.totals.instandsetzungen || 0) +
                                  (user.totals.retouren || 0)
                                }}
                                Stk.</span
                              >
                            </div>
                          </v-card-text>
                        </v-card>
                      </div>

                      <!-- Chart für visuellen Vergleich -->
                      <div class="comparison-chart-container">
                        <canvas
                          id="comparisonChart"
                          ref="comparisonChart"
                        ></canvas>
                      </div>
                    </v-window-item>

                    <!-- Instandsetzungen-Tab -->
                    <v-window-item value="instandsetzungen">
                      <v-card class="comparison-detail-card">
                        <v-card-title class="comparison-detail-title">
                          <v-icon color="blue" class="mr-2">mdi-wrench</v-icon>
                          Instandsetzungen nach Gerätetyp
                        </v-card-title>

                        <v-card-text class="comparison-table-container">
                          <v-table class="comparison-table">
                            <thead>
                              <tr>
                                <th>Gerätetyp</th>
                                <th
                                  v-for="user in comparisonData.users"
                                  :key="user.id"
                                >
                                  {{ user.name }}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="geraetetyp in comparisonData.geraetetypen
                                  .instandsetzungen"
                                :key="geraetetyp"
                              >
                                <td>{{ geraetetyp }}</td>
                                <td
                                  v-for="user in comparisonData.users"
                                  :key="user.id"
                                >
                                  {{
                                    getUserDeviceTypeCount(
                                      user,
                                      "instandsetzungen",
                                      geraetetyp
                                    )
                                  }}
                                </td>
                              </tr>
                              <tr class="comparison-total-row">
                                <td><strong>Gesamt</strong></td>
                                <td
                                  v-for="user in comparisonData.users"
                                  :key="user.id"
                                >
                                  <strong>{{
                                    user.totals.instandsetzungen || 0
                                  }}</strong>
                                </td>
                              </tr>
                            </tbody>
                          </v-table>
                        </v-card-text>
                      </v-card>
                    </v-window-item>

                    <!-- Retouren-Tab -->
                    <v-window-item value="retouren">
                      <v-card class="comparison-detail-card">
                        <v-card-title class="comparison-detail-title">
                          <v-icon color="red" class="mr-2"
                            >mdi-package-variant</v-icon
                          >
                          Retouren nach Gerätetyp
                        </v-card-title>

                        <v-card-text class="comparison-table-container">
                          <v-table class="comparison-table">
                            <thead>
                              <tr>
                                <th>Gerätetyp</th>
                                <th
                                  v-for="user in comparisonData.users"
                                  :key="user.id"
                                >
                                  {{ user.name }}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="geraetetyp in comparisonData.geraetetypen
                                  .retouren"
                                :key="geraetetyp"
                              >
                                <td>{{ geraetetyp }}</td>
                                <td
                                  v-for="user in comparisonData.users"
                                  :key="user.id"
                                >
                                  {{
                                    getUserDeviceTypeCount(
                                      user,
                                      "retouren",
                                      geraetetyp
                                    )
                                  }}
                                </td>
                              </tr>
                              <tr class="comparison-total-row">
                                <td><strong>Gesamt</strong></td>
                                <td
                                  v-for="user in comparisonData.users"
                                  :key="user.id"
                                >
                                  <strong>{{
                                    user.totals.retouren || 0
                                  }}</strong>
                                </td>
                              </tr>
                            </tbody>
                          </v-table>
                        </v-card-text>
                      </v-card>
                    </v-window-item>

                    <!-- Nebentätigkeiten-Tab -->
                    <v-window-item value="nebentaetigkeiten">
                      <v-card class="comparison-detail-card">
                        <v-card-title class="comparison-detail-title">
                          <v-icon color="amber" class="mr-2"
                            >mdi-clock-outline</v-icon
                          >
                          Nebentätigkeiten
                        </v-card-title>

                        <v-card-text>
                          <div class="nebentaetigkeiten-comparison">
                            <div
                              v-for="user in comparisonData.users"
                              :key="user.id"
                              class="user-nebentaetigkeiten"
                            >
                              <h3 class="user-name">{{ user.name }}</h3>

                              <template
                                v-if="user.nebentaetigkeiten.length > 0"
                              >
                                <div
                                  v-for="item in user.nebentaetigkeiten"
                                  :key="item.kommentar"
                                  class="nebentaetigkeiten-item"
                                >
                                  <span class="nebentaetigkeiten-name">{{
                                    item.kommentar
                                  }}</span>
                                  <span class="nebentaetigkeiten-duration">{{
                                    formatDuration(item.dauer)
                                  }}</span>
                                </div>
                                <div class="nebentaetigkeiten-total">
                                  <span class="total-label">Gesamtdauer</span>
                                  <span class="total-value">{{
                                    formatDuration(
                                      user.totals.nebentaetigkeiten_minuten
                                    )
                                  }}</span>
                                </div>
                              </template>

                              <div v-else class="no-data-message">
                                Keine Nebentätigkeiten im ausgewählten Zeitraum
                              </div>
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-window-item>
                  </v-window>
                </v-card-text>
              </v-card>
            </template>
          </div>
        </div>

        <!-- PDF Export Container (unsichtbar) -->
        <div
          id="pdf-export-container"
          ref="pdfExportContainer"
          style="display: none"
        ></div>
      </v-card-text>
    </v-card>

    <!-- Details Modal -->
    <v-dialog v-model="showDetailsModal" max-width="1400px" scrollable>
      <v-card class="details-modal">
        <v-card-title class="d-flex align-center justify-space-between pa-4">
          <div>
            <div class="text-h5">
              <v-icon color="success" class="mr-2">mdi-clipboard-list</v-icon>
              Auftragsdetails
            </div>
            <div class="text-subtitle-2 mt-1" v-if="selectedUserForDetails">
              {{ selectedUserForDetails.name }} - {{ getTimeframeText() }}
            </div>
          </div>
          <v-btn icon @click="closeDetailsModal" variant="text">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-4" style="max-height: 70vh;">
          <div v-if="detailsLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="success"></v-progress-circular>
            <div class="mt-4">Lade Details...</div>
          </div>

          <div v-else-if="periodDetails">
            <!-- Zusammenfassung -->
            <v-card class="mb-4" elevation="2">
              <v-card-title class="text-subtitle-1 pa-3 bg-grey-darken-3">
                <v-icon size="small" class="mr-2">mdi-chart-box</v-icon>
                Zusammenfassung
              </v-card-title>
              <v-card-text class="pa-3">
                <v-row dense>
                  <v-col cols="12" sm="6" md="3">
                    <div class="summary-item">
                      <div class="summary-label">Gesamtmenge</div>
                      <div class="summary-value text-success">{{ periodDetails.summary.total_menge }} Stk.</div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="summary-item">
                      <div class="summary-label">Aufträge gesamt</div>
                      <div class="summary-value">{{ periodDetails.summary.total_auftraege }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="summary-item">
                      <div class="summary-label">Instandsetzungen</div>
                      <div class="summary-value">{{ periodDetails.summary.instandsetzungen_count }} ({{ periodDetails.summary.instandsetzungen_menge }} Stk.)</div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="summary-item">
                      <div class="summary-label">Retouren</div>
                      <div class="summary-value">{{ periodDetails.summary.retouren_count }} ({{ periodDetails.summary.retouren_menge }} Stk.)</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Filter für Auftragsdetails -->
            <v-card class="mb-4" elevation="2">
              <v-card-title class="text-subtitle-1 pa-3 bg-grey-darken-3">
                <v-icon size="small" class="mr-2">mdi-filter</v-icon>
                Filteroptionen
              </v-card-title>
              <v-card-text class="pa-3">
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="filterGeraetetyp"
                      :items="geraetetypFilterOptions"
                      label="Gerätetyp filtern"
                      density="comfortable"
                      variant="outlined"
                      clearable
                      class="form-field"
                      color="success"
                      hide-details
                    >
                      <template v-slot:prepend-inner>
                        <v-icon size="small" color="success">mdi-devices</v-icon>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="filterReparaturtyp"
                      :items="reparaturtypFilterOptions"
                      label="Reparaturtyp filtern"
                      density="comfortable"
                      variant="outlined"
                      clearable
                      class="form-field"
                      color="success"
                      hide-details
                    >
                      <template v-slot:prepend-inner>
                        <v-icon size="small" color="success">mdi-tools</v-icon>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Tabs für verschiedene Kategorien -->
            <v-tabs v-model="detailsTab" color="success" class="mb-4">
              <v-tab value="instandsetzungen">
                <v-icon size="small" class="mr-2">mdi-wrench</v-icon>
                Instandsetzungen ({{ filteredPeriodDetails?.instandsetzungen.length || 0 }})
              </v-tab>
              <v-tab value="retouren">
                <v-icon size="small" class="mr-2">mdi-package-variant</v-icon>
                Retouren ({{ filteredPeriodDetails?.retouren.length || 0 }})
              </v-tab>
              <v-tab value="nebentaetigkeiten">
                <v-icon size="small" class="mr-2">mdi-clock-outline</v-icon>
                Nebentätigkeiten ({{ filteredPeriodDetails?.nebentaetigkeiten.length || 0 }})
              </v-tab>
            </v-tabs>

            <v-window v-model="detailsTab">
              <!-- Instandsetzungen -->
              <v-window-item value="instandsetzungen">
                <v-card elevation="2">
                  <v-data-table
                    :headers="instandsetzungenDetailsHeaders"
                    :items="filteredPeriodDetails?.instandsetzungen || []"
                    :items-per-page="10"
                    class="details-table"
                    density="comfortable">
                    <template v-slot:item.erstelldatum="{ item }">
                      {{ formatDate(item.erstelldatum) }}
                    </template>
                    <template v-slot:item.menge="{ item }">
                      <v-chip size="small" color="success" variant="flat">
                        {{ item.menge }} Stk.
                      </v-chip>
                    </template>
                    <template v-slot:item.notiz="{ item }">
                      <div class="text-truncate" style="max-width: 200px;" :title="item.notiz">
                        {{ item.notiz || '-' }}
                      </div>
                    </template>
                  </v-data-table>
                </v-card>
              </v-window-item>

              <!-- Retouren -->
              <v-window-item value="retouren">
                <v-card elevation="2">
                  <v-data-table
                    :headers="retourenDetailsHeaders"
                    :items="filteredPeriodDetails?.retouren || []"
                    :items-per-page="10"
                    class="details-table"
                    density="comfortable">
                    <template v-slot:item.erstelldatum="{ item }">
                      {{ formatDate(item.erstelldatum) }}
                    </template>
                    <template v-slot:item.menge="{ item }">
                      <v-chip size="small" color="info" variant="flat">
                        {{ item.menge }} Stk.
                      </v-chip>
                    </template>
                    <template v-slot:item.notiz="{ item }">
                      <div class="text-truncate" style="max-width: 200px;" :title="item.notiz">
                        {{ item.notiz || '-' }}
                      </div>
                    </template>
                  </v-data-table>
                </v-card>
              </v-window-item>

              <!-- Nebentätigkeiten -->
              <v-window-item value="nebentaetigkeiten">
                <v-card elevation="2">
                  <v-data-table
                    :headers="nebentaetigkeitenDetailsHeaders"
                    :items="filteredPeriodDetails?.nebentaetigkeiten || []"
                    :items-per-page="10"
                    class="details-table"
                    density="comfortable">
                    <template v-slot:item.startzeit="{ item }">
                      {{ formatDate(item.startzeit) }} {{ formatTime(item.startzeit) }}
                    </template>
                    <template v-slot:item.endzeit="{ item }">
                      {{ formatTime(item.endzeit) }}
                    </template>
                    <template v-slot:item.dauer_minuten="{ item }">
                      <v-chip size="small" color="warning" variant="flat">
                        {{ formatDuration(item.dauer_minuten) }}
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-card>
              </v-window-item>
            </v-window>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useAuthStore } from "@/stores/auth";
import axios from "axios";
import { API_BASE_URL } from "@/config";
import { useToast } from "vue-toastification";
import Chart from "chart.js/auto";
import { jsPDF } from "jspdf";
import ManagementTrendWidget from "./widgets/ManagementTrendWidget.vue";

// ======== Basis-Setup ========
const toast = useToast();
const authStore = useAuthStore();
const loading = ref(false);
const csvLoading = ref(false);
const hasData = ref(false);

// ======== Tab-Navigation ========
const activeTab = ref("summary");
const activeRankingTab = ref("instandsetzungen");
const activeComparisonTab = ref("overview");

// ======== Zeitraum-Filter ========
// Optionen für die Zeitraumauswahl
const timeframeOptions = [
  { title: "Monat", value: "month" },
  { title: "Quartal", value: "quarter" },
  { title: "Jahr", value: "year" },
  { title: "Benutzerdefiniert", value: "custom" },
];

const timeframe = ref("month");

// Monatsoptionen für die Auswahl
const monthOptions = [
  { title: "Januar", value: "01" },
  { title: "Februar", value: "02" },
  { title: "März", value: "03" },
  { title: "April", value: "04" },
  { title: "Mai", value: "05" },
  { title: "Juni", value: "06" },
  { title: "Juli", value: "07" },
  { title: "August", value: "08" },
  { title: "September", value: "09" },
  { title: "Oktober", value: "10" },
  { title: "November", value: "11" },
  { title: "Dezember", value: "12" },
];

// Quartalsoptionen
const quarterOptions = [
  { title: "Q1 (Jan-Mär)", value: "1" },
  { title: "Q2 (Apr-Jun)", value: "2" },
  { title: "Q3 (Jul-Sep)", value: "3" },
  { title: "Q4 (Okt-Dez)", value: "4" },
];

// Jahr-Optionen (die letzten 5 Jahre)
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => ({
    title: String(currentYear - i),
    value: String(currentYear - i),
  }));
});

// Standardwerte setzen
const selectedMonth = ref(String(new Date().getMonth() + 1).padStart(2, "0"));
const selectedQuarter = ref(
  Math.ceil((new Date().getMonth() + 1) / 3).toString()
);
const selectedYear = ref(new Date().getFullYear().toString());
const customStartDate = ref("");
const customEndDate = ref("");

// ======== Benutzerauswahl ========
const users = ref([]);
const selectedUser = ref(null); // für summary tab (einzelner Benutzer)
const selectedUsers = ref([]); // für comparison tab (mehrere Benutzer)

// ======== Daten-Speicher ========
const summaryData = ref(null);
const rankingData = ref(null);
const comparisonData = ref(null);
const comparisonChart = ref(null);

// ======== Berechnete Eigenschaften ========
// Tabellen-Header für Rankings
const instandsetzungenHeaders = [
  { title: "Platz", key: "position", align: "center", width: "80px" },
  { title: "Name", key: "name", align: "start" },
  { title: "Anzahl Aufträge", key: "anzahl_auftraege", align: "center" },
  { title: "Gesamtmenge", key: "gesamt_menge", align: "center" },
];

const retourenHeaders = [
  { title: "Platz", key: "position", align: "center", width: "80px" },
  { title: "Name", key: "name", align: "start" },
  { title: "Anzahl Aufträge", key: "anzahl_auftraege", align: "center" },
  { title: "Gesamtmenge", key: "gesamt_menge", align: "center" },
];

const nebentaetigkeitenHeaders = [
  { title: "Platz", key: "position", align: "center", width: "80px" },
  { title: "Name", key: "name", align: "start" },
  { title: "Anzahl Tätigkeiten", key: "anzahl_taetigkeiten", align: "center" },
  { title: "Gesamtzeit", key: "gesamt_minuten", align: "center" },
];

const gesamtHeaders = [
  { title: "Platz", key: "position", align: "center", width: "80px" },
  { title: "Name", key: "name", align: "start" },
  { title: "Instandsetzungen", key: "instandsetzungen", align: "center" },
  { title: "Retouren", key: "retouren", align: "center" },
  {
    title: "Nebentätigkeiten",
    key: "nebentaetigkeiten_minuten",
    align: "center",
  },
];

const geraetetypHeaders = [
  { title: "Platz", key: "position", align: "center", width: "80px" },
  { title: "Name", key: "name", align: "start" },
  { title: "Anzahl", key: "anzahl", align: "center" },
];

// ======== Hilfsfunktionen ========
// Formatiert einen Zeitwert in Minuten zu einem lesbaren Format
const formatDuration = (minutes) => {
  if (!minutes || isNaN(minutes)) return "0 Min.";

  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);

  if (hours > 0) {
    return `${hours} Std. ${mins} Min.`;
  } else {
    return `${mins} Min.`;
  }
};

// Gibt Klassenname für Ranking-Platzierung zurück
const getRankClass = (index) => {
  if (index === 0) return "rank-first";
  if (index === 1) return "rank-second";
  if (index === 2) return "rank-third";
  return "";
};

// Gibt Namen eines Benutzers anhand der ID zurück
const getUserName = (userId) => {
  if (!userId) return "";
  const user = users.value.find((u) => u.id === userId);
  return user ? user.name : "";
};

// Ermittelt die Anzahl der Instandsetzungen für einen Benutzer
const getTotalInstandsetzungen = (userData) => {
  if (!userData || !userData.instandsetzungen) return 0;
  return userData.instandsetzungen.reduce(
    (sum, item) => sum + (parseInt(item.menge) || 0),
    0
  );
};

// Ermittelt die Anzahl der Retouren für einen Benutzer
const getTotalRetouren = (userData) => {
  if (!userData || !userData.retouren) return 0;
  return userData.retouren.reduce(
    (sum, item) => sum + (parseInt(item.menge) || 0),
    0
  );
};

// Ermittelt die Gesamtzeit der Nebentätigkeiten für einen Benutzer
const getTotalNebentaetigkeiten = (userData) => {
  if (!userData || !userData.nebentaetigkeiten) return 0;
  return userData.nebentaetigkeiten.reduce(
    (sum, item) => sum + (parseInt(item.dauer) || 0),
    0
  );
};

// Formatiert den ausgewählten Zeitraum für die Anzeige
const getTimeframeText = () => {
  if (timeframe.value === "month") {
    const monthName =
      monthOptions.find((m) => m.value === selectedMonth.value)?.title || "";
    return `${monthName} ${selectedYear.value}`;
  } else if (timeframe.value === "quarter") {
    return `${
      quarterOptions.find((q) => q.value === selectedQuarter.value)?.title || ""
    } ${selectedYear.value}`;
  } else if (timeframe.value === "year") {
    return `Jahr ${selectedYear.value}`;
  } else if (timeframe.value === "custom") {
    return `${customStartDate.value} bis ${customEndDate.value}`;
  }
  return "";
};

// Ermittelt die Anzahl eines bestimmten Gerätetyps bei einer Kategorie für einen Benutzer
const getUserDeviceTypeCount = (user, category, deviceType) => {
  if (!user || !user[category]) return 0;

  const item = user[category].find((i) => i.geraetetyp === deviceType);
  return item ? item.menge : 0;
};

// ======== API-Interaktionen ========
// Benutzer laden
const fetchUsers = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/users.php?action=get`, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    if (response.data.status === "success") {
      users.value = response.data.users
        .filter((user) => user.aktiv)
        .map((user) => ({
          id: user.id,
          name: `${user.vorname} ${user.nachname}`,
          loginname: user.loginname,
          mitarbeiternummer: user.mitarbeiternummer,
        }));
    } else {
      toast.error("Fehler beim Laden der Benutzer");
    }
  } catch (error) {
    toast.error("Verbindungsfehler beim Laden der Benutzer");
    console.error(error);
  }
};

// Gerätetypen und Reparaturtypen laden
const fetchTypen = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/categories.php?action=get`, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    if (response.data.status === 'success') {
      geraetetypen.value = response.data.geraetetypen || [];
      reparaturtypen.value = response.data.reparaturtypen || [];
    } else {
      console.error('Fehler beim Laden der Kategorien:', response.data.message);
    }
  } catch (error) {
    console.error('Fehler beim Laden der Typen:', error);
  }
};

// Hilfsfunktion zur Bestimmung des Monats aus dem Quartal
const getMonthFromQuarter = (quarter) => {
  return (parseInt(quarter) - 1) * 3 + 1;
};

// ======== Details Modal ========
const showDetailsModal = ref(false);
const detailsLoading = ref(false);
const periodDetails = ref(null);
const selectedUserForDetails = ref(null);
const detailsTab = ref('instandsetzungen');

// ======== Typ-Filter ========
const filterGeraetetyp = ref(null);
const filterReparaturtyp = ref(null);
const geraetetypen = ref([]);
const reparaturtypen = ref([]);

// Table Headers für Details
const instandsetzungenDetailsHeaders = [
  { title: 'Auftragsnr.', key: 'auftragsnummer', sortable: true },
  { title: 'Datum', key: 'erstelldatum', sortable: true },
  { title: 'Kunde', key: 'kunde', sortable: true },
  { title: 'Gerätetyp', key: 'geraetetyp', sortable: true },
  { title: 'Reparaturtyp', key: 'reparaturtyp', sortable: true },
  { title: 'Menge', key: 'menge', sortable: true },
  { title: 'Notiz', key: 'notiz', sortable: false }
]

const retourenDetailsHeaders = [
  { title: 'Auftragsnr.', key: 'auftragsnummer', sortable: true },
  { title: 'Datum', key: 'erstelldatum', sortable: true },
  { title: 'Kunde', key: 'kunde', sortable: true },
  { title: 'Gerätetyp', key: 'geraetetyp', sortable: true },
  { title: 'Retourentyp', key: 'retourentyp', sortable: true },
  { title: 'Menge', key: 'menge', sortable: true },
  { title: 'Notiz', key: 'notiz', sortable: false }
]

const nebentaetigkeitenDetailsHeaders = [
  { title: 'Startzeit', key: 'startzeit', sortable: true },
  { title: 'Endzeit', key: 'endzeit', sortable: true },
  { title: 'Dauer', key: 'dauer_minuten', sortable: true },
  { title: 'Kommentar', key: 'kommentar', sortable: false }
]

// Computed Properties für Filter-Optionen
const geraetetypFilterOptions = computed(() => {
  return geraetetypen.value.map(typ => ({
    title: typ.name,
    value: typ.id
  }));
});

const reparaturtypFilterOptions = computed(() => {
  return reparaturtypen.value.map(typ => ({
    title: typ.name,
    value: typ.id
  }));
});

// Gefilterte Details für Modal
const filteredPeriodDetails = computed(() => {
  if (!periodDetails.value) return null;

  const filtered = {
    ...periodDetails.value,
    instandsetzungen: periodDetails.value.instandsetzungen || [],
    retouren: periodDetails.value.retouren || [],
    nebentaetigkeiten: periodDetails.value.nebentaetigkeiten || []
  };

  // Filter Instandsetzungen
  if (filterGeraetetyp.value || filterReparaturtyp.value) {
    filtered.instandsetzungen = filtered.instandsetzungen.filter(item => {
      let matches = true;
      if (filterGeraetetyp.value && parseInt(item.geraetetyp_id) !== parseInt(filterGeraetetyp.value)) {
        matches = false;
      }
      if (filterReparaturtyp.value && parseInt(item.reparaturtyp_id) !== parseInt(filterReparaturtyp.value)) {
        matches = false;
      }
      return matches;
    });
  }

  // Filter Retouren
  if (filterGeraetetyp.value || filterReparaturtyp.value) {
    filtered.retouren = filtered.retouren.filter(item => {
      let matches = true;
      if (filterGeraetetyp.value && parseInt(item.geraetetyp_id) !== parseInt(filterGeraetetyp.value)) {
        matches = false;
      }
      if (filterReparaturtyp.value && parseInt(item.retourentyp_id) !== parseInt(filterReparaturtyp.value)) {
        matches = false;
      }
      return matches;
    });
  }

  return filtered;
});

// Details für ausgewählten Zeitraum laden
const showUserDetails = async (userData) => {
  detailsLoading.value = true;
  selectedUserForDetails.value = userData;
  showDetailsModal.value = true;

  try {
    // Berechne Zeitraum basierend auf aktueller Auswahl
    let periodStart, periodEnd;

    if (timeframe.value === 'month') {
      periodStart = `${selectedYear.value}-${selectedMonth.value}-01`;
      const date = new Date(selectedYear.value, selectedMonth.value, 0);
      periodEnd = `${selectedYear.value}-${selectedMonth.value}-${date.getDate()}`;
    } else if (timeframe.value === 'quarter') {
      const startMonth = getMonthFromQuarter(selectedQuarter.value);
      periodStart = `${selectedYear.value}-${String(startMonth).padStart(2, '0')}-01`;
      const endMonth = startMonth + 2;
      const date = new Date(selectedYear.value, endMonth, 0);
      periodEnd = `${selectedYear.value}-${String(endMonth).padStart(2, '0')}-${date.getDate()}`;
    } else if (timeframe.value === 'year') {
      periodStart = `${selectedYear.value}-01-01`;
      periodEnd = `${selectedYear.value}-12-31`;
    } else if (timeframe.value === 'custom') {
      periodStart = customStartDate.value;
      periodEnd = customEndDate.value;
    }

    const payload = {
      action: 'period_details',
      user_id: userData.id,
      period_start: periodStart,
      period_end: periodEnd
    }

    const response = await axios.post(`${API_BASE_URL}admin/management_stats.php`, payload, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (response.data.status === 'success') {
      periodDetails.value = response.data
    } else {
      toast.error('Fehler beim Laden der Details: ' + (response.data.message || 'Unbekannter Fehler'))
    }
  } catch (err) {
    console.error('Fehler beim Laden der Auftragsdetails:', err)
    toast.error('Fehler beim Laden der Auftragsdetails')
  } finally {
    detailsLoading.value = false
  }
}

const closeDetailsModal = () => {
  showDetailsModal.value = false;
  periodDetails.value = null;
  selectedUserForDetails.value = null;
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const formatTime = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

// Daten basierend auf der aktiven Ansicht abrufen
const fetchData = async () => {
  if (!authStore.user || !authStore.user.super_admin) {
    toast.error("Nur Super Admins haben Zugriff auf diese Funktion");
    return;
  }

  loading.value = true;

  let params = {
    timeframe: timeframe.value,
    year: selectedYear.value,
  };

  // Zeitraumparameter entsprechend der Auswahl festlegen
  if (timeframe.value === "month") {
    params.month = selectedMonth.value;
  } else if (timeframe.value === "quarter") {
    params.month = getMonthFromQuarter(selectedQuarter.value);
  } else if (timeframe.value === "custom") {
    params.start_date = customStartDate.value;
    params.end_date = customEndDate.value;
  }

  try {
    if (activeTab.value === "summary") {
      // Zusammenfassungsdaten abrufen
      await fetchSummaryData(params);
    } else if (activeTab.value === "ranking") {
      // Ranking-Daten abrufen
      await fetchRankingData(params);
    } else if (activeTab.value === "comparison") {
      // Prüfen, ob mindestens 2 Benutzer für Vergleich ausgewählt wurden
      if (selectedUsers.value.length < 2) {
        hasData.value = false;
        loading.value = false;
        return;
      }

      // Vergleichsdaten abrufen
      await fetchComparisonData(params);
    }

    hasData.value = true;
  } catch (error) {
    toast.error("Fehler beim Laden der Daten");
    console.error(error);
    hasData.value = false;
  } finally {
    loading.value = false;
  }
};

// Zusammenfassungsdaten laden
const fetchSummaryData = async (params) => {
  try {
    const requestData = {
      action: "summary",
      ...params,
      user_id: selectedUser.value || null,
    };

    const response = await axios.post(
      `${API_BASE_URL}admin/management_stats.php`,
      requestData,
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    if (response.data.status === "success") {
      summaryData.value = response.data;

      // Mitarbeiternummern aus dem users-Array zu den summaryData hinzufügen
      if (summaryData.value && summaryData.value.results) {
        summaryData.value.results.forEach((userData) => {
          // Benutzer im users-Array suchen
          const user = users.value.find((u) => {
            // Namen vergleichen, da die IDs möglicherweise unterschiedlich formatiert sind
            return u.name === userData.name;
          });

          // Mitarbeiternummer hinzufügen, wenn gefunden
          if (user && user.mitarbeiternummer) {
            userData.mitarbeiternummer = user.mitarbeiternummer;
          }
        });
      }
    } else {
      toast.error("Fehler beim Laden der Auswertungsdaten");
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Ranking-Daten laden
const fetchRankingData = async (params) => {
  try {
    const requestData = {
      action: "ranking",
      ...params,
    };

    const response = await axios.post(
      `${API_BASE_URL}admin/management_stats.php`,
      requestData,
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    if (response.data.status === "success") {
      rankingData.value = response.data;
    } else {
      toast.error("Fehler beim Laden der Ranking-Daten");
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Vergleichsdaten laden
const fetchComparisonData = async (params) => {
  try {
    if (!selectedUsers.value || selectedUsers.value.length === 0) {
      toast.warning("Bitte wählen Sie mindestens einen Benutzer aus");
      return;
    }

    const requestData = {
      action: "comparison",
      user_ids: selectedUsers.value,
      ...params,
    };

    const response = await axios.post(
      `${API_BASE_URL}admin/management_stats.php`,
      requestData,
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    if (response.data.status === "success") {
      // Die Originaldaten kopieren
      comparisonData.value = response.data;

      // Das geraetetypen-Objekt für die Verwendung im Template erstellen
      comparisonData.value.geraetetypen = {
        instandsetzungen: response.data.geraetetypen_instandsetzungen || [],
        retouren: response.data.geraetetypen_retouren || [],
      };

      // Totals für jeden Benutzer berechnen und hinzufügen
      if (comparisonData.value && comparisonData.value.users) {
        comparisonData.value.users.forEach((user) => {
          // Totals-Eigenschaft für jeden Benutzer hinzufügen
          user.totals = {
            instandsetzungen: getTotalInstandsetzungen(user),
            retouren: getTotalRetouren(user),
            nebentaetigkeiten_minuten: getTotalNebentaetigkeiten(user),
          };
        });
      }

      // Chart erst erstellen, wenn die Daten vorhanden sind
      if (activeComparisonTab.value === "overview") {
        // Längere Verzögerung für die Chart-Erstellung
        setTimeout(() => {
          destroyComparisonChart(); // Erst sicherstellen, dass kein Chart existiert
          createComparisonChart();
        }, 300); // 300ms Verzögerung
      }
    } else {
      toast.error(
        response.data.message || "Fehler beim Laden der Vergleichsdaten"
      );
    }
  } catch (error) {
    console.error(error);
    toast.error("Verbindungsfehler beim Laden der Vergleichsdaten");
  }
};

// ======== Ereignishandler ========
// Alle Benutzer auswählen (für Summary-Tab)
const selectAllUsers = () => {
  selectedUser.value = null;
};

// ======== Chart-Erstellung ========
// Chart für den Vergleich erstellen
let chartInstance = null; // Chart-Instanz global verfolgen

// Funktion zum Zerstören eines vorhandenen Charts
const destroyComparisonChart = () => {
  if (chartInstance) {
    try {
      chartInstance.destroy();
      chartInstance = null;
    } catch (error) {
      console.error("Fehler beim Zerstören des Charts:", error);
    }
  }
};

// Chart für den Vergleich erstellen
const createComparisonChart = () => {
  // Wenn keine Daten vorhanden sind, abbrechen
  if (
    !comparisonData.value ||
    !comparisonData.value.users ||
    comparisonData.value.users.length === 0
  ) {
    return;
  }

  // Erst sicherstellen dass wir den alten Chart zerstören
  destroyComparisonChart();

  // Dann nextTick abwarten, damit das Canvas-Element vollständig gerendert ist
  nextTick(() => {
    // Canvas-Element suchen
    const chartElement = document.getElementById("comparisonChart");
    if (!chartElement) {
      console.error("Chart-Element nicht gefunden");
      return;
    }

    const labels = ["Instandsetzungen", "Retouren", "Nebentätigkeiten (Std.)"];
    const datasets = comparisonData.value.users.map((user) => {
      // ID für konsistente Farben verwenden
      const userId = user.id || 0;
      const colorSeed = hashString(userId.toString());

      // Hauptfarbe für den Benutzer erzeugen
      const hue = colorSeed % 360;
      const colorBase = `hsl(${hue}, 70%, 60%)`;
      const colorHover = `hsl(${hue}, 75%, 50%)`;

      return {
        label: user.name,
        data: [
          user.totals?.instandsetzungen || 0,
          user.totals?.retouren || 0,
          (user.totals?.nebentaetigkeiten_minuten || 0) / 60, // Minuten in Stunden umwandeln
        ],
        backgroundColor: colorBase,
        borderColor: colorHover,
        borderWidth: 1,
      };
    });

    // Neue Chart erstellen und Referenz speichern
    chartInstance = new Chart(chartElement, {
      type: "bar",
      data: {
        labels: labels,
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
          },
        },
      },
    });
  });
};

// Hilfsfunktion, um eine ID in einen Farbwert zu konvertieren
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

// ======== PDF-Export ========
// PDF für die gesamte Auswertung exportieren
const exportPdf = async () => {
  if (!hasData.value) return;

  toast.info("PDF wird erstellt...");

  // Bestimme den Inhalt basierend auf dem aktiven Tab
  let title;

  if (activeTab.value === "summary") {
    title = `Benutzerauswertung für ${getTimeframeText()}`;
  } else if (activeTab.value === "ranking") {
    title = `Leistungsübersichten für ${getTimeframeText()}`;
  } else if (activeTab.value === "comparison") {
    title = `Benutzervergleich für ${getTimeframeText()}`;
  }

  try {
    // PDF im A4-Format erstellen
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Titel und Kopfzeile
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(title, 15, 20);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(`Erstellt am: ${new Date().toLocaleDateString("de-DE")}`, 15, 30);

    // Tabellen-Position
    let y = 40;

    // Je nach Tab unterschiedliche Tabellen erstellen
    if (activeTab.value === "summary") {
      // Daten für Benutzerauswertung
      if (summaryData.value && summaryData.value.results) {
        // Für jeden Benutzer eine Tabelle erzeugen
        for (const user of summaryData.value.results) {
          // Benutzer-Header
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");
          doc.text(`${user.name} - ${user.mitarbeiternummer}`, 15, y);
          y += 10;

          // Instandsetzungen
          if (user.instandsetzungen.length > 0) {
            doc.setFontSize(12);
            doc.text("Instandsetzungen:", 15, y);
            y += 8;

            // Tabellen-Header
            doc.setFillColor(240, 240, 240);
            doc.rect(15, y - 6, 180, 8, "F");
            doc.setFont("helvetica", "bold");
            doc.text("Gerätetyp", 17, y);
            doc.text("Reparaturtyp", 75, y);
            doc.text("Menge", 160, y);
            y += 6;

            // Tabellen-Inhalt
            doc.setFont("helvetica", "normal");
            for (const item of user.instandsetzungen) {
              // Seitenumbruch, wenn nötig
              if (y > 270) {
                doc.addPage();
                y = 20;
              }

              doc.text(item.geraetetyp || "-", 17, y);
              doc.text(item.reparaturtyp || "-", 75, y);
              doc.text(item.menge?.toString() || "0", 160, y);
              y += 8;
            }
            y += 5;
          }

          // Retouren
          if (user.retouren.length > 0) {
            // Seitenumbruch, wenn nötig
            if (y > 220) {
              doc.addPage();
              y = 20;
            }

            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text("Retouren:", 15, y);
            y += 8;

            // Tabellen-Header
            doc.setFillColor(240, 240, 240);
            doc.rect(15, y - 6, 180, 8, "F");
            doc.text("Gerätetyp", 17, y);
            doc.text("Retourentyp", 75, y);
            doc.text("Menge", 160, y);
            y += 6;

            // Tabellen-Inhalt
            doc.setFont("helvetica", "normal");
            for (const item of user.retouren) {
              if (y > 270) {
                doc.addPage();
                y = 20;
              }

              doc.text(item.geraetetyp || "-", 17, y);
              doc.text(item.retourentyp || "-", 75, y);
              doc.text(item.menge?.toString() || "0", 160, y);
              y += 8;
            }
            y += 5;
          }

          // Nebentätigkeiten
          if (user.nebentaetigkeiten.length > 0) {
            // Seitenumbruch, wenn nötig
            if (y > 220) {
              doc.addPage();
              y = 20;
            }

            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text("Nebentätigkeiten:", 15, y);
            y += 8;

            // Tabellen-Header
            doc.setFillColor(240, 240, 240);
            doc.rect(15, y - 6, 180, 8, "F");
            doc.text("Bezeichnung", 17, y);
            doc.text("Dauer", 160, y);
            y += 6;

            // Tabellen-Inhalt
            doc.setFont("helvetica", "normal");
            for (const item of user.nebentaetigkeiten) {
              if (y > 270) {
                doc.addPage();
                y = 20;
              }

              doc.text(item.kommentar || "Ohne Bezeichnung", 17, y);
              doc.text(formatDuration(item.dauer), 160, y);
              y += 8;
            }

            // Summenzeile
            y += 2;
            doc.setDrawColor(200, 200, 200);
            doc.line(15, y - 4, 195, y - 4);
            doc.setFont("helvetica", "bold");
            doc.text("Gesamt:", 17, y);
            doc.text(formatDuration(getTotalNebentaetigkeiten(user)), 160, y);
            y += 5;
          }

          // Neue Seite für den nächsten Benutzer
          if (
            summaryData.value.results.indexOf(user) <
            summaryData.value.results.length - 1
          ) {
            doc.addPage();
            y = 20;
          }
        }
      } else if (activeTab.value === "ranking") {
        // Leistungsübersichten-Daten
        if (rankingData.value) {
          // Je nach aktivem Ranking-Tab unterschiedliche Tabelle erstellen
          const rankings = rankingData.value.rankings;

          // Titel für das aktuelle Ranking
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");

          if (activeRankingTab.value === "instandsetzungen") {
            doc.text("Instandsetzungen-Leistungsübersicht", 15, y);
            y += 10;

            // Tabellen-Header
            doc.setFillColor(240, 240, 240);
            doc.rect(15, y - 6, 180, 8, "F");
            doc.text("Platz", 17, y);
            doc.text("Name", 40, y);
            doc.text("Aufträge", 100, y);
            doc.text("Menge", 140, y);
            y += 6;

            // Inhalt
            doc.setFont("helvetica", "normal");
            rankings.instandsetzungen.forEach((user, index) => {
              // Seitenumbruch, wenn nötig
              if (y > 270) {
                doc.addPage();
                y = 20;
              }
              doc.text(`${index + 1}.`, 17, y);
              doc.text(
                `${user.vorname} ${user.nachname} - ${user.mitarbeiternummer}`,
                40,
                y
              );
              doc.text(user.anzahl_auftraege?.toString() || "0", 100, y);
              doc.text(user.gesamt_menge?.toString() || "0", 140, y);
              y += 8;
            });
          } else if (activeRankingTab.value === "retouren") {
            // Retouren-Ranking
            doc.text("Retouren-Leistungsübersicht", 15, y);
            y += 10;

            // Tabellen-Header
            doc.setFillColor(240, 240, 240);
            doc.rect(15, y - 6, 180, 8, "F");
            doc.text("Platz", 17, y);
            doc.text("Name", 40, y);
            doc.text("Aufträge", 100, y);
            doc.text("Menge", 140, y);
            y += 6;

            // Inhalt
            doc.setFont("helvetica", "normal");
            rankings.retouren.forEach((user, index) => {
              if (y > 270) {
                doc.addPage();
                y = 20;
              }

              doc.text(`${index + 1}.`, 17, y);
              doc.text(
                `${user.vorname} ${user.nachname} - ${user.mitarbeiternummer}`,
                40,
                y
              );
              doc.text(user.anzahl_auftraege?.toString() || "0", 100, y);
              doc.text(user.gesamt_menge?.toString() || "0", 140, y);
              y += 8;
            });
          } else if (activeRankingTab.value === "nebentaetigkeiten") {
            // Nebentätigkeiten-Ranking
            doc.text("Nebentätigkeiten-Leistungsübersicht", 15, y);
            y += 10;

            // Tabellen-Header
            doc.setFillColor(240, 240, 240);
            doc.rect(15, y - 6, 180, 8, "F");
            doc.text("Platz", 17, y);
            doc.text("Name", 40, y);
            doc.text("Anzahl", 100, y);
            doc.text("Gesamtzeit", 140, y);
            y += 6;

            // Inhalt
            doc.setFont("helvetica", "normal");
            rankings.nebentaetigkeiten.forEach((user, index) => {
              if (y > 270) {
                doc.addPage();
                y = 20;
              }

              doc.text(`${index + 1}.`, 17, y);
              doc.text(
                `${user.vorname} ${user.nachname} - ${user.mitarbeiternummer}`,
                40,
                y
              );
              doc.text(user.anzahl_taetigkeiten?.toString() || "0", 100, y);
              doc.text(formatDuration(user.gesamt_minuten), 140, y);
              y += 8;
            });
          } else if (activeRankingTab.value === "gesamt") {
            // Gesamtranking
            doc.text("Gesamtleistungsübersicht", 15, y);
            y += 10;

            // Tabellen-Header
            doc.setFillColor(240, 240, 240);
            doc.rect(15, y - 6, 180, 8, "F");
            doc.text("Platz", 17, y);
            doc.text("Name", 40, y);
            doc.text("Instandsetz.", 90, y);
            doc.text("Retouren", 120, y);
            doc.text("Nebentät.", 150, y);
            doc.text("Gesamt", 180, y);
            y += 6;

            // Inhalt
            doc.setFont("helvetica", "normal");
            rankings.gesamt.forEach((user, index) => {
              if (y > 270) {
                doc.addPage();
                y = 20;
              }

              doc.text(`${index + 1}.`, 17, y);
              doc.text(
                `${user.vorname} ${user.nachname} - ${user.mitarbeiternummer}`,
                40,
                y
              );
              doc.text(user.instandsetzungen?.toString() || "0", 90, y);
              doc.text(user.retouren?.toString() || "0", 120, y);
              // Zeit formatieren
              const nebentatMinutes = user.nebentaetigkeiten_minuten || 0;
              doc.text(`${Math.round(nebentatMinutes / 60)}h`, 150, y);
              // Gesamtpunktzahl
              doc.text(user.gesamtpunkte?.toFixed(1)?.toString() || "0", 180, y);
              y += 8;
            });
          }
        }
      } else if (activeTab.value === "comparison") {
        // Direktvergleich exportieren
        if (
          comparisonData.value &&
          comparisonData.value.users &&
          comparisonData.value.users.length > 0
        ) {
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");

          if (activeComparisonTab.value === "overview") {
            // Übersichtstabelle
            doc.text("Übersicht", 15, y);
            y += 10;

            // Tabellen-Header
            doc.setFillColor(240, 240, 240);
            doc.rect(15, y - 6, 180, 8, "F");
            doc.text("Name", 17, y);
            doc.text("Instandsetzungen", 80, y);
            doc.text("Retouren", 125, y);
            doc.text("Nebentätigkeiten", 160, y);
            y += 6;

            // Inhalt
            doc.setFont("helvetica", "normal");
            comparisonData.value.users.forEach((user) => {
              if (y > 270) {
                doc.addPage();
                y = 20;
              }

              doc.text(user.name, 17, y);
              doc.text(user.totals?.instandsetzungen?.toString() || "0", 80, y);
              doc.text(user.totals?.retouren?.toString() || "0", 125, y);
              doc.text(
                formatDuration(user.totals?.nebentaetigkeiten_minuten || 0),
                160,
                y
              );
              y += 8;
            });
          } else if (activeComparisonTab.value === "instandsetzungen") {
            // Instandsetzungsvergleich
            doc.text("Instandsetzungen nach Gerätetyp", 15, y);
            y += 10;

            // Spaltenbreite berechnen
            const nameWidth = 50;
            const userColumnCount = comparisonData.value.users.length;
            const userColumnWidth = Math.min(130 / userColumnCount, 40);

            // Tabellen-Header
            doc.setFillColor(240, 240, 240);
            doc.rect(15, y - 6, 180, 8, "F");
            doc.text("Gerätetyp", 17, y);

            // Benutzernamen als Spaltenüberschriften
            comparisonData.value.users.forEach((user, index) => {
              doc.text(
                user.name.substring(0, 10),
                nameWidth + index * userColumnWidth,
                y
              );
            });
            y += 6;

            // Inhalt pro Gerätetyp
            doc.setFont("helvetica", "normal");
            for (const geraetetyp of comparisonData.value.geraetetypen
              .instandsetzungen) {
              if (y > 270) {
                doc.addPage();
                y = 20;
              }

              doc.text(geraetetyp, 17, y);

              // Werte pro Benutzer
              comparisonData.value.users.forEach((user, index) => {
                const count = getUserDeviceTypeCount(
                  user,
                  "instandsetzungen",
                  geraetetyp
                );
                doc.text(
                  count.toString(),
                  nameWidth + index * userColumnWidth,
                  y
                );
              });
              y += 8;
            }

            // Summenzeile
            y += 2;
            doc.setDrawColor(200, 200, 200);
            doc.line(15, y - 4, 195, y - 4);
            doc.setFont("helvetica", "bold");
            doc.text("Gesamt:", 17, y);

            comparisonData.value.users.forEach((user, index) => {
              doc.text(
                user.totals?.instandsetzungen?.toString() || "0",
                nameWidth + index * userColumnWidth,
                y
              );
            });
          } else if (activeComparisonTab.value === "retouren") {
            // Retourenvergleich (ähnlich wie Instandsetzungen)
            doc.text("Retouren nach Gerätetyp", 15, y);
            y += 10;

            // Spaltenbreite berechnen
            const nameWidth = 50;
            const userColumnCount = comparisonData.value.users.length;
            const userColumnWidth = Math.min(130 / userColumnCount, 40);

            // Tabellen-Header
            doc.setFillColor(240, 240, 240);
            doc.rect(15, y - 6, 180, 8, "F");
            doc.text("Gerätetyp", 17, y);

            comparisonData.value.users.forEach((user, index) => {
              doc.text(
                user.name.substring(0, 10),
                nameWidth + index * userColumnWidth,
                y
              );
            });
            y += 6;

            // Inhalt
            doc.setFont("helvetica", "normal");
            for (const geraetetyp of comparisonData.value.geraetetypen.retouren) {
              if (y > 270) {
                doc.addPage();
                y = 20;
              }

              doc.text(geraetetyp, 17, y);

              comparisonData.value.users.forEach((user, index) => {
                const count = getUserDeviceTypeCount(
                  user,
                  "retouren",
                  geraetetyp
                );
                doc.text(
                  count.toString(),
                  nameWidth + index * userColumnWidth,
                  y
                );
              });
              y += 8;
            }

            // Summenzeile
            y += 2;
            doc.setDrawColor(200, 200, 200);
            doc.line(15, y - 4, 195, y - 4);
            doc.setFont("helvetica", "bold");
            doc.text("Gesamt:", 17, y);

            comparisonData.value.users.forEach((user, index) => {
              doc.text(
                user.totals?.retouren?.toString() || "0",
                nameWidth + index * userColumnWidth,
                y
              );
            });
          } else if (activeComparisonTab.value === "nebentaetigkeiten") {
            // Nebentätigkeitenvergleich
            doc.text("Nebentätigkeiten-Vergleich", 15, y);
            y += 10;

            // Für jeden Benutzer eine separate Sektion
            for (const user of comparisonData.value.users) {
              // Seitenumbruch, wenn nötig
              if (y > 220) {
                doc.addPage();
                y = 20;
              }

              // Benutzer-Header
              doc.setFontSize(13);
              doc.setFont("helvetica", "bold");
              doc.text(`${user.name}:`, 15, y);
              y += 8;

              if (user.nebentaetigkeiten && user.nebentaetigkeiten.length > 0) {
                // Tabellen-Header
                doc.setFillColor(240, 240, 240);
                doc.rect(15, y - 6, 180, 8, "F");
                doc.setFontSize(12);
                doc.text("Bezeichnung", 17, y);
                doc.text("Dauer", 160, y);
                y += 6;

                // Tabellen-Inhalt
                doc.setFont("helvetica", "normal");
                for (const item of user.nebentaetigkeiten) {
                  if (y > 270) {
                    doc.addPage();
                    y = 20;
                  }

                  doc.text(item.kommentar || "Ohne Bezeichnung", 17, y);
                  doc.text(formatDuration(item.dauer), 160, y);
                  y += 8;
                }

                // Summenzeile
                y += 2;
                doc.setDrawColor(200, 200, 200);
                doc.line(15, y - 4, 195, y - 4);
                doc.setFont("helvetica", "bold");
                doc.text("Gesamt:", 17, y);
                doc.text(
                  formatDuration(user.totals?.nebentaetigkeiten_minuten || 0),
                  160,
                  y
                );
              } else {
                doc.setFontSize(12);
                doc.setFont("helvetica", "italic");
                doc.text(
                  "Keine Nebentätigkeiten im ausgewählten Zeitraum",
                  17,
                  y
                );
              }

              y += 15; // Abstand zum nächsten Benutzer
            }
          }
        }
      }
    }

    // PDF im neuen Tab öffnen statt herunterzuladen
    const pdfBlob = doc.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);
    window.open(blobUrl, "_blank");

    // Erfolgsmeldung
    toast.success("PDF erstellt und in einem neuen Tab geöffnet");
  } catch (error) {
    console.error("PDF-Export fehlgeschlagen:", error);
    toast.error("Fehler beim PDF-Export");
  }
};

// ======== CSV-Export ========
const exportCsv = async () => {
  if (activeTab.value !== 'summary') {
    toast.error("CSV-Export ist nur für die Mitarbeiter-Übersicht verfügbar.");
    return;
  }

  csvLoading.value = true;

  try {
    // API-Aufruf für CSV-Export
    const response = await axios.post(
      `${API_BASE_URL}admin/management_stats.php`,
      {
        action: 'csv_export',
        timeframe: timeframe.value,
        month: selectedMonth.value,
        year: selectedYear.value,
        ...(timeframe.value === 'custom' && {
          from: customStartDate.value,
          to: customEndDate.value
        })
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        },
        responseType: 'blob' // Wichtig für Datei-Download
      }
    );

    // CSV-Datei als Blob herunterladen
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Dateiname aus Content-Disposition Header extrahieren oder Standard verwenden
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'Management_Statistik.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    toast.success('CSV-Datei wurde erfolgreich heruntergeladen');
  } catch (error) {
    console.error('CSV-Export fehlgeschlagen:', error);

    // Spezielle Behandlung für Axios-Fehler mit Blob-Response
    if (error.response && error.response.data && error.response.data instanceof Blob) {
      // Blob zu Text konvertieren, um JSON-Fehler zu lesen
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        toast.error('Fehler beim CSV-Export: ' + (errorData.message || 'Unbekannter Fehler'));
      } catch {
        toast.error('Fehler beim CSV-Export: Server-Fehler');
      }
    } else if (error.response && error.response.data && typeof error.response.data === 'object') {
      // Normale JSON-Fehlerantwort
      toast.error('Fehler beim CSV-Export: ' + (error.response.data.message || 'Unbekannter Fehler'));
    } else {
      // Netzwerk- oder andere Fehler
      toast.error('Fehler beim CSV-Export: ' + (error.message || 'Netzwerkfehler'));
    }
  } finally {
    csvLoading.value = false;
  }
};

// ======== Lebenszyklus-Hooks ========
// Bei Komponenten-Mount Daten laden
onMounted(() => {
  // Überprüfen, ob der aktuelle Benutzer ein Super-Admin ist
  const isUserSuperAdmin = authStore.user && authStore.user.super_admin;

  if (!isUserSuperAdmin) {
    toast.error(
      "Zugriff verweigert. Diese Seite ist nur für Super-Admins verfügbar."
    );
    // Optional: Umleitung zur Startseite o.ä.
    return;
  }

  fetchUsers();
  fetchTypen();
  comparisonChart.value = null;
  destroyComparisonChart();
});

// Tabs beobachten und bei Änderung Daten neu laden
watch(activeTab, () => {
  if (hasData.value) {
    fetchData();
  }
});

// Beim Tab-Wechsel Chart zerstören
watch(activeTab, (newTab, oldTab) => {
  if (oldTab === "comparison") {
    destroyComparisonChart();
  }

  if (newTab === "comparison" && comparisonData.value) {
    // Wir verzögern die Chart-Erstellung leicht, um sicherzustellen,
    // dass das DOM aktualisiert ist
    setTimeout(() => {
      if (activeComparisonTab.value === "overview") {
        createComparisonChart();
      }
    }, 50);
  }
});

// Bei Änderung der Vergleichs-Tabs Chart neu erstellen
watch(activeComparisonTab, (newTab, oldTab) => {
  if (oldTab === "overview") {
    destroyComparisonChart();
  }

  if (newTab === "overview" && comparisonData.value) {
    // Wir verzögern die Chart-Erstellung leicht, um sicherzustellen,
    // dass das DOM aktualisiert ist
    setTimeout(createComparisonChart, 50);
  }
});
</script>

<style scoped>
.management-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

.main-card {
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.management-header {
  padding: 8px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: 1.4rem;
  font-weight: 500;
  margin: 0;
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Alle anderen Stile bleiben unverändert */

/* ======== Grundlegendes Layout ======== */
.management-view {
  position: relative;
  padding-bottom: 20px;
}

/* ======== Hintergrundelemente ======== */
.background-elements {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.ambient-light {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.15;
}

.ambient-light-1 {
  width: 700px;
  height: 700px;
  top: -200px;
  right: -100px;
  animation: pulse 15s infinite alternate;
}

.ambient-light-2 {
  width: 600px;
  height: 600px;
  bottom: -100px;
  left: -100px;
  animation: pulse 20s infinite alternate-reverse;
}

.ambient-light-3 {
  width: 500px;
  height: 500px;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 18s infinite alternate;
}

@keyframes pulse {
  0% {
    opacity: 0.1;
    transform: scale(1);
  }
  50% {
    opacity: 0.15;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.1;
    transform: scale(1);
  }
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

/* ======== Hauptkarten ======== */
.main-card {
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  z-index: 2;
}

/* ======== Header-Bereich ======== */
.management-header {
  margin-bottom: 20px;
  padding: 24px;
  border-radius: 18px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  -webkit-background-clip: text;
  background-clip: text;
  letter-spacing: 0.5px;
}

.subtitle {
  margin-top: 5px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
  letter-spacing: 0.3px;
}

/* ======== Tabs-Navigation ======== */
.management-tabs {
  background: rgba(35, 35, 45, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.tab-item {
  transition: all 0.3s ease;
  letter-spacing: 0.3px;
  font-weight: 500;
  opacity: 0.8;
  text-transform: none;
  border-radius: 4px 4px 0 0;
  margin: 0 2px;
}

.tab-item:hover {
  background: rgba(76, 175, 80, 0.1);
  opacity: 1;
}

.tab-item.v-tab--selected {
  background: rgba(76, 175, 80, 0.15);
  opacity: 1;
  font-weight: 600;
}

/* ======== Filter-Bereich ======== */
.filter-card {
  background: rgba(30, 30, 40, 0.6) !important;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.filter-card:hover {
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
}

.filter-title {
  font-size: 18px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
}

.filter-controls {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 10px;
}

@media (min-width: 768px) {
  .filter-controls {
    grid-template-columns: 1fr 1fr;
  }
}

@media (min-width: 1200px) {
  .filter-controls {
    grid-template-columns: 2fr 2fr 1fr;
  }
}

.timeframe-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
}

.form-field {
  border-radius: 8px;
  transition: all 0.2s ease;
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.9);
}

.form-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.form-field:hover :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

.timeframe-select {
  width: 100%;
  max-width: 200px;
}

.month-select,
.quarter-select,
.year-select {
  width: calc(50% - 6px);
  min-width: 120px;
}

.year-select-wide {
  width: 100%;
  max-width: 200px;
}

.date-select {
  width: calc(50% - 6px);
  min-width: 150px;
}

.user-selector {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.type-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
}

.type-filters .form-field {
  flex: 1;
  min-width: 200px;
}

.selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selector-title {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.show-more-button {
  font-size: 0.85rem;
  opacity: 0.9;
  transition: all 0.3s ease;
}

.show-more-button:hover {
  opacity: 1;
  transform: translateY(-1px);
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.refresh-btn {
  height: 40px;
  border-radius: 10px;
  font-weight: 500;
  letter-spacing: 0.3px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.2) !important;
  transition: all 0.3s ease;
}

.refresh-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.3) !important;
}

.export-btn {
  height: 40px;
  border-radius: 10px;
  font-weight: 500;
  letter-spacing: 0.3px;
  text-transform: none;
  transition: all 0.3s ease;
}

.export-btn:hover {
  transform: translateY(-2px);
}

/* ======== Loading-Overlay ======== */
.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(25, 25, 30, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border-radius: 12px;
  padding: 40px 20px;
  min-height: 300px;
}

.loading-text {
  margin-top: 15px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
}

/* ======== No-Data-Anzeige ======== */
.no-data-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: rgba(30, 30, 40, 0.5);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  min-height: 300px;
}

.no-data-card h3 {
  margin: 16px 0 8px;
  font-size: 20px;
  color: rgba(255, 255, 255, 0.8);
}

.no-data-card p {
  max-width: 400px;
  color: rgba(255, 255, 255, 0.6);
}

/* ======== Tab-Inhalts-Bereiche ======== */
.tab-content-area {
  margin-top: 20px;
  animation: fadeIn 0.3s ease-out;
}

/* ======== Summary Tab ======== */
.summary-header-card {
  background: rgba(30, 30, 40, 0.6) !important;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.user-metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.user-metrics-card {
  background: rgba(30, 30, 40, 0.5) !important;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.user-metrics-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
}

.user-card-header {
  border-bottom: 1px solid rgba(76, 175, 80, 0.2);
  display: flex;
  align-items: center;
  padding: 16px !important;
}

.metrics-section {
  margin-bottom: 20px;
  border-radius: 10px;
  padding: 15px;
  background: rgba(25, 25, 30, 0.6);
}

.metrics-section:last-child {
  margin-bottom: 0;
}

.metrics-title {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.metrics-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metric-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 10px;
  background: rgba(35, 35, 45, 0.6);
  border-radius: 6px;
  transition: background 0.2s ease;
}

.metric-item:hover {
  background: rgba(40, 40, 50, 0.8);
}

.metric-name {
  color: rgba(255, 255, 255, 0.8);
}

.metric-value {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 8px;
  border-radius: 4px;
  background: rgba(20, 20, 25, 0.5);
}

.metric-total {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.total-label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.total-value {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  padding: 2px 10px;
  border-radius: 6px;
  background: rgba(76, 175, 80, 0.2);
}

.no-metrics {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
}

/* Zusammenfassungs-Karte für alle Benutzer */
.summary-totals {
  grid-column: 1 / -1;
}

.totals-header {
  border-bottom: 1px solid rgba(76, 175, 80, 0.3);
}

.totals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 10px;
}

.total-box {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: rgba(30, 30, 40, 0.7);
  border-radius: 10px;
  transition: transform 0.2s ease;
}

.total-box:hover {
  transform: translateY(-2px);
}

.total-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(20, 20, 25, 0.8);
}

.total-data {
  display: flex;
  flex-direction: column;
}

.total-value {
  font-size: 24px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.total-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 5px;
}

/* ======== Ranking Tab ======== */
.ranking-tab {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ranking-header-card {
  background: rgba(30, 30, 40, 0.6) !important;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.ranking-title {
  font-size: 18px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
}

.ranking-tab-nav {
  background: rgba(35, 35, 45, 0.5);
  border-radius: 8px;
}

.ranking-table-card {
  background: rgba(30, 30, 40, 0.5) !important;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.ranking-table-title {
  padding: 16px;
  font-size: 17px;
  font-weight: 500;
  background: rgba(25, 25, 30, 0.7);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.table-container {
  padding: 0;
  overflow-x: auto;
}

.ranking-table {
  width: 100%;
}

.rank-cell {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-weight: 600;
  background: rgba(30, 30, 40, 0.7);
}

.rank-first {
  background: linear-gradient(135deg, #ffd700, #ffa000);
  color: #333;
  box-shadow: 0 2px 10px rgba(255, 215, 0, 0.4);
}

.rank-second {
  background: linear-gradient(135deg, #c0c0c0, #a0a0a0);
  color: #333;
  box-shadow: 0 2px 8px rgba(192, 192, 192, 0.4);
}

.rank-third {
  background: linear-gradient(135deg, #cd7f32, #a05a2c);
  color: #fff;
  box-shadow: 0 2px 8px rgba(205, 127, 50, 0.4);
}

.device-type-card {
  background: rgba(30, 30, 40, 0.5) !important;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.device-expansion-panel {
  background: rgba(25, 25, 30, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  margin-bottom: 8px;
  border-radius: 8px;
  overflow: hidden;
}

/* ======== Vergleichs-Tab ======== */
.comparison-tab {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.comparison-card {
  background: rgba(30, 30, 40, 0.5) !important;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.comparison-tab-nav {
  background: rgba(35, 35, 45, 0.5);
  border-radius: 8px;
}

.comparison-overview {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.user-comparison-card {
  background: rgba(25, 25, 30, 0.7) !important;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.user-comparison-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
}

.user-comparison-summary {
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.comparison-metric {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: rgba(35, 35, 45, 0.6);
  border-radius: 8px;
}

.metric-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.8);
}

.metric-value {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.comparison-chart-container {
  height: 400px;
  margin: 20px 0;
  padding: 15px;
  background: rgba(25, 25, 30, 0.7);
  border-radius: 10px;
  position: relative;
}

.comparison-detail-card {
  background: rgba(30, 30, 40, 0.5) !important;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
  margin-top: 15px;
}

.comparison-detail-title {
  padding: 16px;
  font-size: 17px;
  font-weight: 500;
  background: rgba(25, 25, 30, 0.7);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.comparison-table-container {
  padding: 15px;
  overflow-x: auto;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
}

.comparison-table th {
  background: rgba(25, 25, 30, 0.8);
  padding: 12px 15px;
  text-align: left;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
}

.comparison-table td {
  padding: 12px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.comparison-table tr:last-child td {
  border-bottom: none;
}

.comparison-total-row {
  background: rgba(30, 30, 40, 0.8);
}

.comparison-total-row td {
  border-top: 2px solid rgba(255, 255, 255, 0.1);
  font-weight: 600;
}

.nebentaetigkeiten-comparison {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  padding: 10px;
}

.user-nebentaetigkeiten {
  background: rgba(25, 25, 30, 0.7);
  border-radius: 10px;
  padding: 15px;
}

.user-name {
  font-size: 16px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

.nebentaetigkeiten-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: rgba(35, 35, 45, 0.6);
  border-radius: 6px;
}

.nebentaetigkeiten-name {
  color: rgba(255, 255, 255, 0.8);
}

.nebentaetigkeiten-duration {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 193, 7, 0.2);
  padding: 2px 8px;
  border-radius: 4px;
}

.nebentaetigkeiten-total {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.no-data-message {
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  padding: 20px;
  font-style: italic;
}

/* ======== Tooltip Styles ======== */
.hoverable {
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.hoverable:hover {
  color: rgba(76, 175, 80, 0.9);
}

.info-icon {
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.hoverable:hover .info-icon {
  opacity: 1;
  color: rgba(76, 175, 80, 1);
}

.tooltip-content {
  max-width: 300px;
  padding: 0;
  background: rgba(25, 25, 30, 0.95);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.tooltip-header {
  background: rgba(76, 175, 80, 0.2);
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.tooltip-body {
  padding: 12px 16px;
}

.tooltip-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.tooltip-item:last-child {
  border-bottom: none;
}

.tooltip-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  flex: 1;
}

.tooltip-value {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  font-size: 13px;
  background: rgba(76, 175, 80, 0.15);
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
}

/* ======== Animationen ======== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.show-all-container,
.show-less-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.user-name-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.user-fullname {
  font-weight: 500;
}

.user-id {
  font-size: 0.85em;
  opacity: 0.7;
  margin-top: -2px;
}

.user-id-inline {
  font-size: 0.9em;
  opacity: 0.7;
  margin-left: 4px;
}

/* ======== Responsive Anpassungen ======== */
@media (max-width: 768px) {
  .management-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 15px;
  }

  .page-title {
    font-size: 24px;
  }

  .subtitle {
    font-size: 14px;
  }

  .filter-controls {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .timeframe-selector,
  .user-selector,
  .action-buttons {
    width: 100%;
  }

  .timeframe-select,
  .month-select,
  .quarter-select,
  .year-select,
  .year-select-wide,
  .date-select {
    width: 100%;
    max-width: none;
  }

  .user-metrics-grid {
    grid-template-columns: 1fr;
  }

  .comparison-overview {
    grid-template-columns: 1fr;
  }

  .comparison-chart-container {
    height: 300px;
  }

  .nebentaetigkeiten-comparison {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .ranking-title {
    font-size: 16px;
  }

  .comparison-detail-title {
    font-size: 15px;
  }

  .total-box {
    padding: 15px;
  }

  .total-icon {
    width: 40px;
    height: 40px;
  }

  .total-value {
    font-size: 20px;
  }
}

/* Trend-Sektion Styling */
.trend-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 16px;
}

.trend-section .management-trend-widget {
  background: rgba(25, 25, 35, 0.8) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

/* Details Modal Styles */
.details-modal {
  background: rgba(25, 25, 30, 0.98) !important;
}

.summary-item {
  text-align: center;
  padding: 8px;
}

.summary-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.details-table {
  background: transparent !important;
}

.details-table :deep(th) {
  background: rgba(76, 175, 80, 0.15) !important;
  color: rgba(255, 255, 255, 0.9) !important;
  font-weight: 600 !important;
}

.details-table :deep(td) {
  color: rgba(255, 255, 255, 0.85) !important;
}

.details-table :deep(tr:hover) {
  background: rgba(76, 175, 80, 0.08) !important;
}

/* Ende des Stylesheets */
</style>
